import PropTypes from 'prop-types';
import {
  CardHeader,
  Row,
  GgxNumber,
  OrderStatusDesc,
  OrderStatusType,
  StatusIcon,
  TrackingCard,
  TitleRow,
  ContentCollapse,
  CustomDivider,
  ExpandIcon,
  HeaderTime,
  MobileVerticle,
  ProgressIcon,
  CustomStepLabel,
  CustomStepMobileContent,
} from '@/modules/track-and-trace/track-record-card/track-record-card.styles';
import { useEffect, useState } from 'react';
import { displayTimeStringWithoutYear } from '@/utilities/reusable-functions/display-util';
import TrackRecordRow from '@/modules/track-and-trace/track-record-row/track-record-row.component';
import InTransitSvg from '@/assets/in-transit-filled.svg';
import { Stepper } from '@gogovan/entier-web-core/ext/mui/Stepper';
import { Step } from '@gogovan/entier-web-core/ext/mui/Step';
import { StepConnector } from '@gogovan/entier-web-core/ext/mui/StepConnector';
import { useMediaQuery } from '@gogovan/entier-web-core/ext/mui/useMediaQuery';
import { setupGlobalTimeZone } from '@gogovan/entier-web-core/utilities/date-time-util';
import { tntTheme as theme } from '@/utilities/theme';
import { WorkflowStatusType } from '@gogovan/entier-web-core/enums/workflow-status-type.enum';
import { Box } from '@gogovan/entier-web-core/ext/mui/Box';

const statusColorMapping = {
  [WorkflowStatusType.CREATED]: '#0088BF',
  [WorkflowStatusType.USER_DEFINED]: '#0088BF',
  [WorkflowStatusType.EXCEPTION]: '#EF5350',
  [WorkflowStatusType.DELIVERED]: '#4CAF50',
  [WorkflowStatusType.COMPLETED]: '#B4B4B4',
  [WorkflowStatusType.CANCELLED]: '#B4B4B4',
};

const TrackRecordCard = ({ trackingRecords }) => {
  const [orderStatusIcon, setOrderStatusIcon] = useState(<div />);
  const [openDetail, setOpenDetail] = useState(false);
  const shiftTrackingRecords = JSON.parse(JSON.stringify(trackingRecords));
  const isWindowSize = useMediaQuery(theme.breakpoints.up('sm'));
  shiftTrackingRecords.shift();

  const latestStatusIconMapping = (stateType) => {
    const statusIconColor = statusColorMapping[stateType] || '#0088BF';
    if (stateType === WorkflowStatusType.DELIVERED)
      setOrderStatusIcon(<StatusIcon color={statusIconColor}>check_circle</StatusIcon>);
    else if (stateType === WorkflowStatusType.EXCEPTION)
      setOrderStatusIcon(<StatusIcon color={statusIconColor}>error</StatusIcon>);
    else if ([WorkflowStatusType.COMPLETED, WorkflowStatusType.CANCELLED].includes(stateType))
      setOrderStatusIcon(<StatusIcon color={statusIconColor}>cancel</StatusIcon>);
    else
      setOrderStatusIcon(
        <StatusIcon>
          <InTransitSvg />
        </StatusIcon>
      );
  };

  useEffect(() => {
    latestStatusIconMapping(trackingRecords[0].stateType);
    setupGlobalTimeZone(trackingRecords[0].timezone);
  }, [trackingRecords]);

  return (
    <TrackingCard>
      <CardHeader
        canClick={shiftTrackingRecords.length > 0}
        onClick={() => {
          setOpenDetail(!openDetail);
        }}
      >
        <Row>
          {orderStatusIcon}
          <MobileVerticle>
            <Box sx={{ width: '100%' }}>
              <TitleRow>
                <GgxNumber>{trackingRecords[0].orderId}</GgxNumber>
                <OrderStatusType color={statusColorMapping[trackingRecords[0].stateType] || '#0088BF'}>
                  {trackingRecords[0].statusType}
                </OrderStatusType>
              </TitleRow>
              <OrderStatusDesc>{trackingRecords[0].description}</OrderStatusDesc>
            </Box>
            <HeaderTime>{displayTimeStringWithoutYear(new Date(trackingRecords[0].createDateTime))}</HeaderTime>
          </MobileVerticle>
        </Row>
        {shiftTrackingRecords.length > 0 && <ExpandIcon openDetail={openDetail}>expand_more</ExpandIcon>}
      </CardHeader>
      {shiftTrackingRecords.length > 0 && (
        <ContentCollapse in={openDetail} timeout='auto' unmountOnExit>
          <CustomDivider />
          <Stepper orientation='vertical'>
            <StepConnector sx={{ height: '0px' }} />
            {shiftTrackingRecords.map((trackingRecord, index) => (
              <Step active>
                {isWindowSize ? (
                  <CustomStepLabel icon={<ProgressIcon />}>
                    {/* eslint-disable-next-line react/no-array-index-key */}
                    <TrackRecordRow trackingRecord={trackingRecord} key={index} />
                  </CustomStepLabel>
                ) : (
                  <>
                    <CustomStepLabel icon={<ProgressIcon />}>
                      {/* eslint-disable-next-line react/no-array-index-key */}
                      <TrackRecordRow trackingRecord={trackingRecord} key={index} noContent />
                    </CustomStepLabel>
                    <CustomStepMobileContent>
                      {/* eslint-disable-next-line react/no-array-index-key */}
                      <TrackRecordRow trackingRecord={trackingRecord} key={index} noHeader />
                    </CustomStepMobileContent>
                  </>
                )}
              </Step>
            ))}
          </Stepper>
        </ContentCollapse>
      )}
    </TrackingCard>
  );
};

TrackRecordCard.propTypes = {
  trackingRecords: PropTypes.array.isRequired,
};

export default TrackRecordCard;
